import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";

export function NotificationBell() {
  const { session, authedFetch } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const popoverRef = useRef(null);

  async function refresh() {
    if (!session) return;
    try {
      const out = await authedFetch("/api/notifications/me?limit=20");
      setItems(out.notifications || []);
      setUnread(out.unread || 0);
    } catch { /* ignore */ }
  }

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [session]);

  useEffect(() => {
    function onClick(e) {
      if (open && popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    }
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  async function markAll() {
    try { await authedFetch("/api/notifications/read-all", { method: "POST" }); refresh(); } catch {}
  }
  async function archiveRead() {
    try { await authedFetch("/api/notifications/archive-read", { method: "POST" }); refresh(); } catch {}
  }
  async function archiveAll() {
    const ok = window.confirm("Clear all notifications? This will remove read and unread notifications from this list.");
    if (!ok) return;
    try { await authedFetch("/api/notifications/archive-all", { method: "POST" }); refresh(); } catch {}
  }
  async function markOne(id) {
    try { await authedFetch(`/api/notifications/${id}/read`, { method: "POST" }); refresh(); } catch {}
  }
  async function archiveOne(id) {
    try { await authedFetch(`/api/notifications/${id}/archive`, { method: "POST" }); refresh(); } catch {}
  }

  function plainNotificationText(value = "") {
    const doc = new DOMParser().parseFromString(String(value), "text/html");
    return (doc.body.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  if (!session) return null;

  return (
    <div ref={popoverRef} style={{ position: "relative" }}>
      <button onClick={() => { setOpen(!open); refresh(); }} aria-label={`Notifications (${unread} unread)`}
        style={{
          background: "none", border: "none", padding: 4, margin: 0,
          color: "var(--text)", cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          position: "relative", flexShrink: 0, lineHeight: 1, fontSize: 20,
        }}>
        🔔
        {unread > 0 && (
          <span aria-hidden="true" style={{
            position: "absolute", top: -3, right: -3, minWidth: 16, height: 16,
            borderRadius: 8, background: "var(--gold)", color: "#000",
            fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center",
            justifyContent: "center", padding: "0 3px",
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div role="dialog" aria-label="Notifications" style={{
          position: "absolute", top: 44, right: 0,
          width: "min(340px, calc(100vw - 28px))",
          maxHeight: "min(440px, calc(100svh - 88px))",
          background: "var(--bg-elevated)", color: "var(--text)",
          borderRadius: 16, border: "1px solid var(--border)",
          boxShadow: "var(--shadow-lg)", overflow: "hidden", zIndex: 50,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap",
            padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
            <strong style={{ fontFamily: "var(--font-display)" }}>Notifications</strong>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Link to="/notifications" onClick={() => setOpen(false)} style={{
                fontSize: 12, fontWeight: 700, color: "var(--accent)", textDecoration: "none",
              }}>View all</Link>
              <button onClick={markAll} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 600, color: "var(--accent)",
              }}>Mark read</button>
              <button onClick={archiveRead} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 600, color: "var(--text-muted)",
              }}>Clear read</button>
              <button onClick={archiveAll} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 700, color: "var(--danger, #b42318)",
              }}>Clear all</button>
            </div>
          </div>
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {items.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                You're all caught up.
              </div>
            ) : items.map((n) => (
              <div key={n.id} role="button" tabIndex={0} onClick={() => {
                setExpandedId(expandedId === n.id ? null : n.id);
                if (!n.read_at) markOne(n.id);
              }} onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setExpandedId(expandedId === n.id ? null : n.id);
                  if (!n.read_at) markOne(n.id);
                }
              }} style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "12px 14px", border: "none",
                borderBottom: "1px solid var(--border)",
                background: n.read_at ? "transparent" : "rgba(196,135,154,.08)",
                cursor: "pointer", color: "var(--text)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{n.subject || "Notification"}</div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveOne(n.id);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--text-muted)",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                </div>
                {n.body && (
                  <div style={{
                    fontSize: 12.5,
                    color: "var(--text-muted)",
                    marginTop: 6,
                    lineHeight: 1.55,
                    display: "-webkit-box",
                    WebkitLineClamp: expandedId === n.id ? "unset" : 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}>
                    {plainNotificationText(n.body)}
                  </div>
                )}
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                  {new Date(n.sent_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
